import React, { FC, useEffect, useRef, useState } from "react";
import {
	Grid,
	GridCellProps,
	GridColumn,
	GridColumnProps,
	GridDetailRowProps,
	GridEvent,
	GridFilterChangeEvent,
	GridFilterOperators,
	GridRowClickEvent,
	GridRowProps,
	GridSortChangeEvent,
} from "@progress/kendo-react-grid";
import styles from "styles.module.css";
import { CustomRowRender } from "ui/grid/CustomGrid";
import { GridPDFExport } from "@progress/kendo-react-pdf";
import {
	ExcelExport,
	ExcelExportProps,
} from "@progress/kendo-react-excel-export";
import {
	DetailExpandDescriptor,
	setGroupIds,
	SortSettings,
} from "@progress/kendo-react-data-tools";

//@ts-ignore
import "./styles.css";
import { Skeleton } from "@progress/kendo-react-indicators";
import { lastColWithBtn } from "./lastColumnts";
import Info from "ui/Info";
import { OverLayLoading } from "ui/Loading";
import { useInfoStore } from "store/error";
import { CustomError, IconButtonActionType } from "interface";
import { PageAccessByMenu } from "interface/user";
import { checkActionButton } from "utils/helper";
import { handleFilteredItem } from "utils/statUtils";
import { process } from "@progress/kendo-data-query";
import {
	IntlProvider,
	load,
	LocalizationProvider,
	loadMessages,
} from "@progress/kendo-react-intl";
import i18n from "i18n";
import { messagesEn, messagesNo } from "./utilities/localization";
import { COLOR } from "utils/constant";
import CustomModal from "ui/modal/Modal";
import { useTranslation } from "react-i18next";

//const unUsedHeight = 370; // manually adjusted height

loadMessages(messagesEn, "en");
loadMessages(messagesNo, "no");
export interface ColumnButtonProps {
	onclick?: (props: GridCellProps) => void;
	iconName: string;
	title?: string;
	showConditionValue?: any;
	conditionField?: string;
	alwaysShow?: boolean;
	style?: React.CSSProperties;
	disabled?: boolean;
	actionType?: IconButtonActionType;
}
interface ColumnButtonPropsFn extends ColumnButtonProps {
	handler?: (props: GridCellProps) => Omit<ColumnButtonProps, "iconName"> & {
		iconName?: string;
		title?: string;
	};
}
interface GridPageProps {
	filter?: any;
	dataKey?: string;
	isExpandable?: boolean;
	hideScroll?: boolean;
	forceUpdate?: boolean;
	showLoader?: boolean;
	gridName?: string;
	unUsedHeight?: number;
	exportGridAsPDFRef?: React.RefObject<GridPDFExport>;
	exportGridAsExcelRef?: React.RefObject<ExcelExport>;
	excelProps?: Partial<ExcelExportProps>;

	gridColumns: GridColumnProps[];
	itemAboveHeight?: number;
	lastColBtns?: ColumnButtonPropsFn[];
	lastColWidth?: string;
	showDetailIcon?: boolean;
	detailIconTitle?: string;
	pageAccess?: PageAccessByMenu | null;
	// showFilter?: boolean;
	detailPage?: React.ComponentType<GridDetailRowProps>;
	apiMethod: (filter: any) => Promise<any>;
	deleteItem?: {
		api: (id: any) => Promise<any>;
		info: { title: string; description: string };
		showConditionValue?: any;
		conditionField?: string;
	};
	gridHeight?: number;
	gridMaxHeight?: number;
	handelShowRowBgColor?: (rowData: GridRowProps) => string;
	handelShowRowTextColor?: (rowData: GridRowProps) => string;
	getloadedData?: (data: any[]) => void;
	serverFilter?: boolean;
	filterable?: boolean;
	serverFilterMap?: {
		/// The field name that the server expects
		serverFieldName: string;
		/// The filter field on the grid
		filterFieldName: string;
	}[];
	filterOperators?: GridFilterOperators;
	sortable?: SortSettings;
	handleSortChange?: (e: GridSortChangeEvent) => void;
	serverSortValue?: Object;
}

const pageSize = 60;
const expectedTotal = 700;

const GridCustomPage: FC<GridPageProps> = ({
	dataKey = "",
	filter = Object(),
	exportGridAsExcelRef,
	exportGridAsPDFRef,
	showDetailIcon = true,
	lastColWidth = "50px",
	isExpandable = true,
	hideScroll = false,
	gridHeight = null,
	showLoader = true,
	gridName = null,
	itemAboveHeight = 50,
	handelShowRowBgColor,
	handelShowRowTextColor = () => COLOR.Grey20,
	unUsedHeight = 370,
	forceUpdate,
	serverFilter = false,
	serverFilterMap = [],
	filterable = false,
	...props
}) => {
	const [gridData, setGridData] = useState<any[]>([]);
	const requestInProgress = React.useRef(false);
	const deletingItemRef = React.useRef<any>();
	const [showDelete, setShowDelete] = useState(false);
	const [pageHeight, setPageHeight] = useState(window.innerHeight);
	const [loading, setLoading] = useState(false);
	const [detailExpand, setDetailExpand] = useState<
		DetailExpandDescriptor | undefined
	>();

	const overlayRef = useRef<HTMLDivElement>(null);
	const internalFilter = useRef<any>(null);

	const gridRef = useRef<HTMLDivElement>(null);

	const pageRef = useRef({
		dataFinished: false,
		resetData: false,
		nextSkip: 0,
		expectedTotal: expectedTotal,
	});
	const { setError } = useInfoStore();

	// const [dataState, setDataState] = React.useState<State>({});

	const detailRef = useRef({ isOpen: false, index: 0, lastOpenKey: "" });
	useEffect(() => {
		const updateHeight = () => setPageHeight(window.innerHeight);
		window.addEventListener("resize", updateHeight);

		// Cleanup listener on unmount
		return () => window.removeEventListener("resize", updateHeight);
	}, []);

	const requestData = (skip: number, take: number) => {
		if (!props.apiMethod) {
			return;
		}

		if (props.pageAccess && !props.pageAccess?.read) {
			return;
		}

		if (requestInProgress.current || pageRef.current.dataFinished) return;
		requestInProgress.current = true;
		if (showLoader) {
			setLoading(true);
		}
		let internalFilterValue = props.serverSortValue || {};
		// if (!serverFilter) {
		// 	internalFilterValue = {};
		// }
		props
			.apiMethod({
				...filter,
				//				...internalFilterValue,
				skip,
				take,
			})
			.then((response) => {
				pageRef.current.expectedTotal = response.totalCount;
				if (response.data === null) {
					response.data = [];
				}
				if (response.data.length === 0 && !pageRef.current.resetData) {
					pageRef.current.dataFinished = true;
				} else {
					if (props.getloadedData) {
						props.getloadedData(response.data);
					}
					pageRef.current.nextSkip = skip + take;
					if (pageRef.current.resetData) {
						setGridData([...response.data]);
					} else {
						setGridData((prevGridData) => [
							...prevGridData,
							...response.data,
						]);
					}
					pageRef.current.resetData = false;
				}
				requestInProgress.current = false;
			})
			.catch((error) => {
				requestInProgress.current = false;
				if (error.response?.data) {
					if (error.response.data?.errors) {
						setError(new CustomError(error.response?.data.title));
					} else {
						setError(new CustomError(error.response?.data.returnText));
					}
				} else {
					setError(new CustomError(error.message));
				}
			})
			.finally(() => {
				requestInProgress.current = false;
				setLoading(false);
			});
	};

	useEffect(() => {
		pageRef.current.resetData = true;
		pageRef.current.dataFinished = false;

		requestData(0, pageSize);
	}, [filter, forceUpdate]);

	useEffect(() => {
		// if (props.apiMethod && serverFilter) {
		// 	props
		// 		.apiMethod(filter)
		// 		.then((data) => {
		// 			const newDataState = process(data, dataState);
		// 			setGroupIds({ data: newDataState.data, group: dataState.group });
		// 			setResult(newDataState);
		// 		})
		// 		.catch((error) => {
		// 			console.error("Error fetching data:", error);
		// 			if (error.response?.data) {
		// 				setError(new CustomError("Something went wrong", "error"));
		// 			} else {
		// 				setError(new CustomError(error.message));
		// 			}
		// 		});
		// }
	}, []);

	useEffect(() => {
		// if (!props.serverSortValue) return;
		if (internalFilter.current) {
			internalFilter.current = {
				...internalFilter.current,
				...(props.serverSortValue || {}),
			};
		} else {
			internalFilter.current = props.serverSortValue;
		}
		pageRef.current.resetData = true;
		requestData(0, pageSize);
	}, [JSON.stringify(props.serverSortValue || {})]);

	const handleLastColBtns = (): ColumnButtonPropsFn[] => {
		let lastColBtn = props.lastColBtns || [];
		if (props.lastColBtns && props.lastColBtns.length > 0 && props.pageAccess) {
			lastColBtn = props.lastColBtns.filter((btn) => {
				if (!btn.actionType) return true;
				return checkActionButton(btn.actionType, props.pageAccess);
			});
			props.lastColBtns = lastColBtn;
		}

		return lastColBtn;
	};

	const expandDetail = (e: GridRowClickEvent) => {
		//console.log("expandDetail", e);
		if (!showDetailIcon) {
			return;
		}
		const key = e.dataItem[dataKey];
		if (!key) {
			//console.log("No data key found in the data item.");
			return;
		}
		if (!detailRef.current.isOpen) {
			setDetailExpand({ [key]: true });
			detailRef.current = {
				isOpen: detailRef.current.isOpen,
				index: 0,
				lastOpenKey: key,
			};
		} else {
			if (detailRef.current.lastOpenKey === key) {
				setDetailExpand({ [key]: false });
				detailRef.current = {
					isOpen: false,
					index: 0,
					lastOpenKey: "",
				};
			}
		}

		// openCloseDetail(e.dataItem);
	};

	const handleOpenDetail = (e: GridCellProps) => {
		if (!showDetailIcon) {
			return;
		}
		const key = e.dataItem[dataKey];
		if (!key) {
			//console.log("No data key found in the data item.");
			return;
		}
		if (!detailRef.current.isOpen) {
			setDetailExpand({ [key]: true });
			detailRef.current = {
				isOpen: true,
				index: e.dataIndex,
				lastOpenKey: key,
			};
		} else {
			if (detailRef.current.lastOpenKey === key) {
				setDetailExpand({ [key]: false });
				detailRef.current = {
					isOpen: false,
					index: 0,
					lastOpenKey: "",
				};
			} else {
				setDetailExpand({ [key]: true });
				detailRef.current = {
					isOpen: true,
					index: e.dataIndex,
					lastOpenKey: key,
				};
			}
		}
	};

	const handleFetchData = (e: HTMLDivElement) => {
		if (e.scrollHeight - e.scrollTop < e.offsetHeight * 2) {
			requestData(pageRef.current.nextSkip, pageSize);
		}
	};

	const handleOnScroll = (e: GridEvent) => {
		const el = e.nativeEvent.srcElement as HTMLDivElement;
		handleFetchData(el);
	};

	const initDelete = (pros: GridCellProps) => {
		deletingItemRef.current = pros.dataItem;
		setShowDelete(true);
	};

	const handleDelete = () => {
		if (!deletingItemRef.current || !props.deleteItem) {
			return;
		}
		const id = deletingItemRef.current[dataKey];
		props.deleteItem
			.api(id)
			.then(() => {
				setGridData(gridData.filter((item) => item[dataKey] !== id));
				setShowDelete(false);
			})
			.catch((error: any) => {
				console.log(error);
			});
	};

	let deleteItem: any;

	if (props.deleteItem && props.pageAccess?.delete) {
		deleteItem = {
			btn: initDelete,
			showConditionValue: props.deleteItem.showConditionValue,
			conditionField: props.deleteItem.conditionField,
		};
	}

	const handleFilterChange = (e: GridFilterChangeEvent) => {
		handleFilterMap(e);
	};

	const handleFilterMap = (e: GridFilterChangeEvent) => {
		if (!e.filter) {
			pageRef.current.resetData = true;
			internalFilter.current = {};
			requestData(0, pageSize);
			return;
		}
		if (handleFilteredItem) {
			const filters = handleFilteredItem(e.filter);

			let mapObject: any = {};

			filters.forEach((filter) => {
				const map = serverFilterMap.find(
					(map) => map.filterFieldName === filter.field,
				);
				if (map) {
					mapObject[map.serverFieldName] = filter.values[0];
				}
			});

			internalFilter.current = mapObject;
			pageRef.current.resetData = true;
			requestData(0, pageSize);
		}
	};

	const handleOnSortChange = (e: GridSortChangeEvent) => {
		if (e.sort) {
			if (props.handleSortChange) {
				props.handleSortChange(e);

				// let mapObject: any = {};

				// internalFilter.current = mapObject;
				// pageRef.current.resetData = true;
				// requestData(0, pageSize);
				return;
			}
		} else {
			//

			const newDataState = process(gridData, e.sort);
			setGridData(newDataState.data);
			// setGroupIds({ data: newDataState.data });
		}
	};

	const otherClassName = props.detailPage
		? styles.tableColgroupColFirstChild
		: "";

	//const currentHeight = pageHeight - unUsedHeight - itemAboveHeight;
	const currentHeight = gridHeight == null ? pageHeight - unUsedHeight - itemAboveHeight : "100%";

	const scrollable = gridHeight == null ? "scrollable" : "none";
	const { t } = useTranslation();

	const resolvedDetailIconTitle = props.detailIconTitle ?? t("ShowDetails");

	return (
		<div style={{ height: currentHeight }}>
			<div ref={overlayRef}>
				<OverLayLoading show={loading} />
			</div>
			<div style={{ marginTop: 30 }} ref={gridRef}>
				<ExcelExport
					data={gridData || []}
					ref={exportGridAsExcelRef}
					{...(props.excelProps || {})}
				>
					{/* <LocalizationProvider language={i18n.language}> */}
					{/* <IntlProvider locale={i18n.language}> */}
					<Grid
						style={{
							maxHeight: currentHeight,
							paddingBottom: "30px",
							overflow: "auto",
						}}
						rowHeight={34}
						rows={{
							data: (row, props) =>
								CustomRowRender(
									row,
									handelShowRowBgColor,
									handelShowRowTextColor,
								),
						}}
						dataItemKey={dataKey}
						filterable={filterable}
						filterOperators={props.filterOperators}
						sortable={props.sortable}
						// expandField={isExpandable ? "expanded" : ""}
						// detailExpand={isExpandable ? "expanded" : ""}
						onFilterChange={handleFilterChange}
						onSortChange={handleOnSortChange}
						onDataStateChange={(e) => {
							console.log(e);
						}}
						data={gridData || []}
						// data={gridData.slice(0, gridData.length)}
						pageSize={pageSize}
						onScroll={handleOnScroll}
						//scrollable= {scrollable}
						// cells={{ data: loadingCell }}
						detail={props.detailPage}
						detailExpand={detailExpand}
						// editField="inEdit"

						className={
							otherClassName + ` ${hideScroll ? styles.HideScroll : ""}`
						}
						// onRowClick={expandDetail}
					>
						{props.gridColumns.map((column, key) => (
							<GridColumn key={key} {...column} className="no-border" />
						))}
						<GridColumn
							filterable={false}
							{...lastColWithBtn({
								width: lastColWidth,
								showDetails: showDetailIcon,
								openDetail: handleOpenDetail,
								items: handleLastColBtns(),
								showDelete: props.deleteItem ? true : false,
								deleteItem,
								detailIconTitle: resolvedDetailIconTitle,
							})}
						/>
					</Grid>
					{/* </IntlProvider> */}
					{/* </LocalizationProvider> */}
				</ExcelExport>
				{props.deleteItem && showDelete && (
					<CustomModal
						title={props.deleteItem.info.title}
						onClose={() => setShowDelete(false)}
						actions={[
							{
								label: t("Cancel"),
								onClick: () => setShowDelete(false),
								bgColor: COLOR.Green,
								color: "white",
							},
							{
								label: t("Yes"),
								onClick: handleDelete,
								bgColor: COLOR.StatusRed,
								color: "white",
							},
						]}
					>
						<p style={{ padding: "16px 0" }}>{props.deleteItem.info.description}</p>
					</CustomModal>
				)}
			</div>
			<GridPDFExport ref={exportGridAsPDFRef}>
				<Grid data={gridData} pageable={true}>
					{props.gridColumns.map((column, key) => (
						<GridColumn key={key} {...column} />
					))}
				</Grid>
			</GridPDFExport>
		</div>
	);
};

export default GridCustomPage;

const loadingCell = (tdElement: any, props?: any) => {
	//console.log("Loading cell for field:", tdElement);
	const field = tdElement.field || "";
	if (tdElement.dataItem[field] === undefined) {
		return (
			<td>
				<Skeleton
					shape={"text"}
					style={{
						width: "100%",
					}}
				/>
			</td>
		);
	}
	return tdElement;
};
